import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, User, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { requestInviteCode, signUpErrorMessage } from '../../services/betaSignUp';

const MAX_NOTE_LENGTH = 1000;

export default function RequestInviteCodePage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestInviteCode({ name: name.trim(), email: email.trim(), note: note.trim() });
      setSubmitted(true);
    } catch (err: unknown) {
      console.error('Invite code request failed', err);
      setError(signUpErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-6 text-center">
        <div className="flex justify-center">
          <CheckCircle className="w-16 h-16 text-green-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Request received</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Thanks! We'll email you a code if there's room in the beta.
        </p>
        <Link to="/" className="inline-block text-sm text-primary hover:text-primary-hover font-medium">
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Request an Invite Code</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          VoteBeats is invite-only during the beta
        </p>
      </div>

      {error && (
        <div role="alert" className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="input pl-10"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input pl-10"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="note" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Tell us about you and how you'd use VoteBeats
          </label>
          <textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={MAX_NOTE_LENGTH}
            rows={4}
            className="input"
            required
          />
        </div>

        <button type="submit" className="btn btn-primary w-full" disabled={loading}>
          <Send className="w-5 h-5 mr-2" />
          {loading ? 'Sending...' : 'Request a code'}
        </button>
      </form>

      <p className="text-center text-sm text-gray-600 dark:text-gray-400">
        Already have a code?{' '}
        <Link to="/register" className="text-primary hover:text-primary-hover font-medium">
          Create your account
        </Link>
      </p>
      <p className="text-center text-sm text-gray-600 dark:text-gray-400">
        <Link to="/" className="text-primary hover:text-primary-hover font-medium">
          Sign in
        </Link>
      </p>
    </div>
  );
}
